import React, { useEffect, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Alert,
  useTheme,
  alpha,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import API from '@Services/apiAxios';
import { initialDateRange } from '../../../pages/PurchaseInvoice/constants';
import moment from 'moment';
import DateRangePicker from '@Components/DateRangePicker';
import { Shipment } from '../types';
import { Link, useNavigate } from 'react-router-dom';
import { SHIPMENT_STATUS } from '@Constants/Shipment';
import SbButton from '@Components/Button';
import Image from '@Components/Image';

interface Props {
  carrierId: string;
  shipments: Shipment[];
  setShipments: React.Dispatch<React.SetStateAction<Shipment[]>>;
  selectedShipments: Shipment[];
  setSelectedShipments: React.Dispatch<React.SetStateAction<Shipment[]>>;
}

const Step2UnbilledShipments: React.FC<Props> = ({
  carrierId,
  shipments,
  setShipments,
  selectedShipments,
  setSelectedShipments,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupedShipments, setGroupedShipments] = useState<Record<string, Shipment[]>>({});
  const [notification, setNotification] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const theme = useTheme();
  const [dateRange, setDateRange] = useState({ ...initialDateRange });

  const onDateChange = useCallback(({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
    if (!isOpen) setDateRange({ startDate, endDate });
  }, []);

  const fetchShipments = useCallback(async () => {
    if (!carrierId) return;
    setLoading(true);
    setError(null);

    try {
      const fromDate = moment(dateRange.startDate).format('DD/MM/YYYY');
      const toDate = moment(dateRange.endDate).format('DD/MM/YYYY');
      const { data } = await API.get(`/invoice/purchase-invoice/unbilled-debit-shipments`, {
        params: { fromDate, toDate, carrierId },
      });

      const grouped: Record<string, Shipment[]> = data?.data || {};
      setGroupedShipments(grouped);

      const allShipments = Object.values(grouped).flat();
      setShipments(allShipments);
    } catch (error) {
      console.error(error);
      setError('Failed to load shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [carrierId, dateRange, setShipments]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  // Get the current selected group (if any)
  const getCurrentSelectedGroup = useCallback(() => {
    if (selectedShipments.length === 0) return null;

    for (const [invoiceNumber, group] of Object.entries(groupedShipments)) {
      if (
        selectedShipments.some(selected =>
          group.some(groupItem => groupItem.shipmentNumber === selected.shipmentNumber),
        )
      ) {
        return invoiceNumber;
      }
    }
    return null;
  }, [selectedShipments, groupedShipments]);

  const toggleSelect = (shipment: Shipment) => {
    const currentSelectedGroup = getCurrentSelectedGroup();
    const shipmentGroup = Object.keys(groupedShipments).find(groupKey =>
      groupedShipments[groupKey].some(s => s.shipmentNumber === shipment.shipmentNumber),
    );

    // If user is trying to select from a different group when another group is already selected
    if (currentSelectedGroup && shipmentGroup !== currentSelectedGroup) {
      setNotification({
        open: true,
        message: `You can only select shipments from one group at a time. Currently selected group: ${currentSelectedGroup}`,
      });
      return;
    }

    setSelectedShipments(prev =>
      prev.some(s => s.shipmentNumber === shipment.shipmentNumber)
        ? prev.filter(s => s.shipmentNumber !== shipment.shipmentNumber)
        : [...prev, shipment],
    );
  };

  const toggleSelectGroup = (invoiceNumber: string) => {
    const currentSelectedGroup = getCurrentSelectedGroup();

    // If user is trying to select a different group when another group is already selected
    if (currentSelectedGroup && currentSelectedGroup !== invoiceNumber && selectedShipments.length > 0) {
      setNotification({
        open: true,
        message: `You can only select shipments from one group at a time. Please deselect current group (${currentSelectedGroup}) first.`,
      });
      return;
    }

    const groupShipments = groupedShipments[invoiceNumber] || [];
    const allSelected = groupShipments.every(sh => selectedShipments.some(s => s.shipmentNumber === sh.shipmentNumber));

    if (allSelected) {
      setSelectedShipments(prev => prev.filter(s => !groupShipments.some(g => g.shipmentNumber === s.shipmentNumber)));
    } else {
      const newOnes = groupShipments.filter(g => !selectedShipments.some(s => s.shipmentNumber === g.shipmentNumber));
      setSelectedShipments(prev => [...prev, ...newOnes]);
    }
  };

  const formatCurrency = (amount = 0, currency = 'INR') =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);

  const handleCloseNotification = () => {
    setNotification({ open: false, message: '' });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
        sx={{ background: alpha(theme.palette.primary.main, 0.02), borderRadius: 2 }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading Unbilled Shipments...
        </Typography>
      </Box>
    );
  }

  const handleBulkMenuClick = (_event: React.MouseEvent<HTMLElement>) =>
    navigate('/purchase-invoices/bulk-debit-creation', {
      state: {
        carrierId,
        shipments,
      },
    });

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <DateRangePicker onChange={onDateChange} value={dateRange} />
        <SbButton
          startIcon={<Image src={'/images/file_upload.svg'} alt="upload" />}
          onClick={handleBulkMenuClick}
          sx={{ borderRadius: '8px !important', minWidth: '225px' }}
          variant="outlined"
        >
          Bulk Create New
        </SbButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box my={'2rem'} pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'220px'}>
          <Typography className="md-text" fontWeight={700}>
            Select Unbilled Shipments
          </Typography>
        </Box>
        <Chip
          sx={{
            paddingX: '20px',
            color: 'rgba(31, 30, 97, 1)',
            fontWeight: 400,
            lineHeight: '16px',
            fontSize: '14px',
            backgroundColor: 'rgba(244, 243, 247, 1)',
          }}
          label={`${shipments.length} shipments`}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseNotification} severity="warning" sx={{ width: '100%' }}>
          {notification.message}
        </MuiAlert>
      </Snackbar>

      {Object.keys(groupedShipments).length === 0 ? (
        <Box textAlign="center" py={6}>
          <LocalShipping sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">
            No unbilled shipments found
          </Typography>
        </Box>
      ) : (
        Object.entries(groupedShipments).map(([invoiceNumber, group]) => {
          const allSelected = group.every(sh => selectedShipments.some(s => s.shipmentNumber === sh.shipmentNumber));
          const partiallySelected =
            group.some(sh => selectedShipments.some(s => s.shipmentNumber === sh.shipmentNumber)) && !allSelected;

          const currentSelectedGroup = getCurrentSelectedGroup();
          const isDisabledGroup = currentSelectedGroup !== null && currentSelectedGroup !== invoiceNumber;

          return (
            <Accordion
              key={invoiceNumber}
              disableGutters
              elevation={0}
              sx={{
                mb: 2,
                borderLeft: `1px solid rgba(244, 243, 247, 1)`,
                borderRight: `1px solid rgba(244, 243, 247, 1)`,
                borderTop: `1px solid rgba(224, 214, 224, 1)`,
                borderBottom: `1px solid rgba(224, 214, 224, 1)`,
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': { display: 'none' },
                opacity: isDisabledGroup ? 0.6 : 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'rgba(244, 243, 247, 1)',
                  px: 2.5,
                  py: 1.5,
                  minHeight: 'unset !important',
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    alignItems: 'center',
                  },
                }}
              >
                <Box display="flex" alignItems="center" width="98%" gap={2}>
                  <Box flex={1} gap={2}>
                    <Typography
                      className="md-text has-text-grey-light"
                      color={isDisabledGroup ? 'text.disabled' : 'rgba(31, 30, 97, 1)'}
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="16px"
                    >
                      Invoice: {invoiceNumber}
                      {currentSelectedGroup === invoiceNumber && (
                        <Chip label="Currently Selected" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography
                      color={isDisabledGroup ? 'text.disabled' : 'rgba(31, 30, 97, 1)'}
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="16px"
                      sx={{ marginTop: '15px' }}
                    >
                      Shipments({group.length})
                    </Typography>
                  </Box>
                  <Typography
                    color={isDisabledGroup ? 'text.disabled' : 'primary'}
                    lineHeight="16px"
                    sx={{
                      justifySelf: 'flex-end',
                      alignSelf: 'center',
                      fontWeight: 600,
                      fontSize: '1rem',
                      '@media (max-width: 600px)': {
                        fontSize: '0.875rem',
                        width: '100%',
                        textAlign: 'left',
                      },
                    }}
                  >
                    {`Total Amount : ${formatCurrency(
                      group.reduce((sum, sh) => sum + (sh.amount || 0), 0),
                      group[0]?.currency || 'INR',
                    )}`}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Paper
                  elevation={0}
                  sx={{
                    // borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid  rgba(244, 243, 247, 1)`,
                  }}
                >
                  <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 60 }}>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={partiallySelected}
                            disabled={isDisabledGroup}
                            onClick={e => {
                              e.stopPropagation();
                              toggleSelectGroup(invoiceNumber);
                            }}
                            sx={{
                              color: isDisabledGroup ? 'text.disabled' : theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: isDisabledGroup ? 'text.disabled' : theme.palette.primary.main,
                              },
                            }}
                          />
                        </TableCell>
                        {['Shipment No', 'Status', 'Origin', 'Destination', 'Weight', 'Amount'].map((head, i) => (
                          <TableCell className="md-text" key={i} sx={{ py: 1.5, fontWeight: 600 }}>
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.map(sh => {
                        const isSelected = selectedShipments.some(s => s.shipmentNumber === sh.shipmentNumber);
                        const isDisabled =
                          currentSelectedGroup !== null && currentSelectedGroup !== invoiceNumber && !isSelected;

                        return (
                          <TableRow
                            key={sh.shipmentNumber}
                            hover={!isDisabled}
                            onClick={() => !isDisabled && toggleSelect(sh)}
                            sx={{
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              background: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                              opacity: isDisabled ? 0.5 : 1,
                            }}
                          >
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => !isDisabled && toggleSelect(sh)}
                                sx={{
                                  color: isDisabled ? 'text.disabled' : theme.palette.primary.main,
                                  '&.Mui-checked': {
                                    color: isDisabled ? 'text.disabled' : theme.palette.primary.main,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell className="md-text">
                              <Box display="flex" alignItems="center" gap={1}>
                                {/* <LocalShipping sx={{ fontSize: 18 }} /> */}
                                <Link
                                  to={`/shipment/admin/edit/${sh.shipmentNumber}`}
                                  className="sb-text-black underline"
                                >
                                  {sh.shipmentNumber}
                                </Link>
                              </Box>
                            </TableCell>
                            <TableCell className="md-text">{SHIPMENT_STATUS[sh.status]?.label}</TableCell>
                            <TableCell className="md-text">{sh.origin}</TableCell>
                            <TableCell className="md-text">{sh.destination}</TableCell>
                            <TableCell className="md-text">{sh.weight} kg</TableCell>
                            <TableCell className="md-text">{formatCurrency(sh.amount, sh.currency)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default Step2UnbilledShipments;
